import type { FeatureCollection, Geometry } from 'geojson';
import Pbf from 'pbf';
import geobuf from 'geobuf';
import { uniqueCountBy } from './util';

export type CountryProperties = {
    featurecla: string;
    scalerank: number;
    LABELRANK?: number;
    SOVEREIGNT?: string;
    SOV_A3?: string;
    ADM0_DIF?: number;
    LEVEL?: number;
    TYPE?: string;
    TLC?: string;
    ADMIN?: string;
    ADM0_A3?: string;
    GEOU_DIF?: number;
    GEOUNIT?: string;
    GU_A3?: string;
    SU_DIF?: number;
    SUBUNIT?: string;
    SU_A3?: string;
    BRK_DIFF?: number;
    NAME?: string;
    NAME_LONG?: string;
    BRK_A3?: string;
    BRK_NAME?: string;
    BRK_GROUP?: string | null;
    ABBREV?: string;
    POSTAL?: string;
    FORMAL_EN?: string;
    FORMAL_FR?: string | null;
    FORMAL_ES?: string | null;
    NAME_CIAWF?: string;
    NOTE_ADM0?: string | null;
    NOTE_BRK?: string | null;
    NAME_SORT?: string;
    NAME_EN?: string;
    CONTINENT?: string;
    coordinates?: number[][][] | number[][][][] | null;
};

export interface CountryData {
    id: string;
    sovereignId: string;
    name: string;
    longName?: string;
    formalName?: string;
    geometry: GeoJSON.Geometry;
    centroid?: GeoJSON.Position;
    nameList: string[];
    featurecla: string;
    continent: string;
}

function mapContinent(raw: string | undefined): string {
    if (raw === 'North America' || raw === 'South America') return 'America';
    if (raw === 'Europe') return 'Europe';
    if (raw === 'Africa') return 'Africa';
    if (raw === 'Asia') return 'Asia';
    if (raw === 'Oceania') return 'Oceania';
    return 'Other';
}

/** Quiz-specific alternative names keyed by ADM0_A3 code. */
const ALTERNATIVE_NAMES = new Map<string, string[]>([
    ['ARE', ['UAE']],
    ['GBR', ['Great Britain', 'UK']],
    ['KNA', ['kitts and nevis', 'st kitts and nevis']],
    ['VCT', ['vincent and the grenadines', 'st vincent and the grenadines']],
    ['COG', ['Congo']],
    ['COD', ['DRC', 'Democratic Republic of the Congo']],
    ['CAR', ['Central African Republic']],
    ['USA', ['USA', 'America']]
]);

/** Maps a GeoJSON feature collection into the CountryData format used by the quiz. */
export function buildCountryData(
    geojson: FeatureCollection<Geometry, CountryProperties>,
): CountryData[] {
    const result: CountryData[] =  geojson.features.map(feature => {
        const p = feature.properties;
        const altNames = ALTERNATIVE_NAMES.get(p.ADM0_A3!) ?? [];

        const nameList = [p.NAME, p.FORMAL_EN, p.NAME_LONG, p.NAME_CIAWF, p.NAME_SORT, p.NAME_EN, ...altNames]
            .filter((n): n is string => Boolean(n))
            .map(n => n.trim().toLowerCase());

        return {
            id: p.ADM0_A3!,
            sovereignId: p.SOV_A3!,
            name: p.NAME!,
            longName: p.NAME_LONG,
            formalName: p.FORMAL_EN,
            geometry: feature.geometry,
            nameList,
            featurecla: p.featurecla,
            continent: mapContinent(p.CONTINENT)
        };
    });

    console.log(`Found ${geojson.features.length} features total`);
    console.log(`Found ${uniqueCountBy(geojson.features, feature => feature.properties.SOV_A3)} features with unique SOV_A3 codes. (Sovereign codes)`);
    console.log(`Found ${uniqueCountBy(geojson.features, feature => feature.properties.SU_A3)} features with unique SU_A3 codes. (Subunit codes)`);
    console.log(`Found ${uniqueCountBy(geojson.features, feature => feature.properties.ADM0_A3)} features with unique ADM0_A3 codes. (Administrative codes)`);

    return result;
}

export async function fetchCountryDataGeobuf(): Promise<FeatureCollection<Geometry, CountryProperties> | undefined> {
    const response = await fetch('./data.pbf');
    if (!response.ok) {
        console.error('Failed to fetch country data');
        return;
    }
    const buffer = await response.arrayBuffer();
    return geobuf.decode(new Pbf(buffer)) as FeatureCollection<Geometry, CountryProperties>;
}
